import Head from "next/head"
import { useRouter } from "next/router"
import React, { useEffect, useReducer } from "react"
import { replace, t } from "translations"

import Button from "@mui/material/Button"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { MAX_WORD_COUNT, normalizeQuery } from "@nhi/utils"
import { IMegamenuCategory } from "data/contentData/interfaces/IMegamenuCategory"
import { IContainerCard } from "data/contentData/interfaces/mongodb/IContainerCard"
import useQueryData from "data/hooks/useQueryData"
import { search } from "data/searchManager/api/search"
import {
    ISearchFacet,
    ISearchHit,
    ISearchRoot
} from "data/searchManager/interfaces/ISearch"
import { getKeyedArray } from "lib/getKeyedArray"
import { getStaticLayoutProps } from "lib/getStaticLayoutProps"

import Icon from "Components/Icon/Icon"
import Layout from "Components/Layout/Layout"
import TopFacetSelect, {
    FacetResult
} from "Components/Search/SearchFacetSelect"
import SearchRadioGroup from "Components/Search/SearchRadioGroup"
import SearchResult from "Components/Search/SearchResult"
import { ISearchState, searchReducer } from "Components/Search/searchReducer"
import useUserCategory from "Components/UserCategoryModal/useUserCategory"

export default function Search({
    footer,
    megamenu
}: {
    footer: IContainerCard[]
    megamenu: IMegamenuCategory[]
}) {
    const router = useRouter()

    const { userCategory } = useUserCategory()
    const isProfessionalUser = userCategory === "professional"

    const searchQuery = (router.query.q as string)?.trim() ?? ""
    const normalizedQuery = normalizeQuery(searchQuery, "search")
    const NUMBER_OF_ARTICLES_TO_LOAD = 25

    const [state, dispatch] = useReducer(searchReducer, {
        query: normalizedQuery,
        totalHits: 0,
        searchResultList: undefined,
        selectedFacet: "",
        initialFacets: [] as ISearchFacet[],
        skip: 0,
        sortType: 0,
        shouldFetch: true
    } as ISearchState)

    const {
        query,
        totalHits,
        searchResultList,
        selectedFacet,
        initialFacets,
        skip,
        sortType,
        shouldFetch
    } = state as ISearchState

    const {
        data: searchResultFromApi,
        error,
        isLoading
    } = useQueryData<ISearchRoot | undefined>(
        () =>
            shouldFetch
                ? `search-${query}-${selectedFacet}-${NUMBER_OF_ARTICLES_TO_LOAD}-${skip}-${sortType}`
                : null,
        () =>
            search(
                normalizedQuery,
                NUMBER_OF_ARTICLES_TO_LOAD,
                skip,
                sortType,
                false,
                selectedFacet,
                isProfessionalUser
            )
    )

    useEffect(() => {
        dispatch({
            type: "NEW_SEARCHQUERY",
            payload: normalizedQuery
        })
    }, [normalizedQuery])

    useEffect(() => {
        if (searchResultFromApi)
            if (skip === 0)
                dispatch({
                    type: "NEW_SEARCHRESULT",
                    payload: {
                        totalHits: searchResultFromApi.totalHits,
                        searchResultList: searchResultFromApi.hits
                    }
                })
            else
                dispatch({
                    type: "LOAD_MORE_SEARCHRESULTS",
                    payload: searchResultFromApi.hits
                })
    }, [searchResultFromApi, skip])

    useEffect(() => {
        if (!initialFacets.length && searchResultFromApi?.facets)
            dispatch({
                type: "SET_INITIAL_FACETS",
                payload: searchResultFromApi.facets
            })
    }, [initialFacets, searchResultFromApi?.facets])

    if (error || !state) return null

    /**
     * updates the dispatch object with the selected facet string
     * @param facet a string containing the facet selected
     */
    function selectFacet(facet: string) {
        dispatch({
            type: "PICK_FACET",
            payload: facet
        })
    }

    /**
     * updates dispatch object with sort type based on selected number
     * @param type number representing the sort type
     */
    function updateSortType(type: number) {
        dispatch({
            type: "PICK_SORT_TYPE",
            payload: type
        })
    }

    /**
     * button handler to update dispatch object's payload and type.
     * @param e event occurs on button click
     */
    function loadMore(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault()
        dispatch({
            type: "TRIGGER_LOAD_MORE",
            payload: skip + NUMBER_OF_ARTICLES_TO_LOAD
        })
    }

    const showFacetLoading = isLoading && !initialFacets.length
    const hasSearchResultHits = !searchResultList
        ? true
        : (searchResultList?.length ?? 0) > 0

    const isQueryTooLong =
        searchQuery && searchQuery.split(" ").length > MAX_WORD_COUNT

    return (
        <Layout
            footer={footer}
            megamenu={megamenu}>
            <Head>
                <title>
                    {t.search.title} | {t.siteTitle}
                </title>
                <meta
                    name="description"
                    content={`${t.search.title} | ${t.siteTitle}`}
                    key="desc"
                />
            </Head>

            {hasSearchResultHits ? (
                <StyledWrapper>
                    <StyledLeftAside>
                        <FacetResult
                            facets={initialFacets}
                            selectFacet={selectFacet}
                            selectedFacet={selectedFacet}
                            showFacetLoading={showFacetLoading}
                        />
                    </StyledLeftAside>

                    <StyledMain>
                        {isLoading || !searchResultList ? (
                            <>
                                {getKeyedArray(20).map(key => (
                                    <Skeleton
                                        key={key}
                                        height={140}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                {isQueryTooLong ? (
                                    <StyledTypographyTooLong variant="body1">
                                        {replace(t.search.tooLong, {
                                            query: normalizedQuery
                                        })}
                                    </StyledTypographyTooLong>
                                ) : null}
                                <SearchResultHeader
                                    hits={totalHits}
                                    hasResult={hasSearchResultHits}
                                    searchQuery={normalizedQuery}
                                />

                                <StyledTopFilterWrapper>
                                    <SearchRadioGroup
                                        updateSortType={updateSortType}
                                        sortValue={sortType}
                                    />
                                    <StyledTopFacetSelectWrapper>
                                        <TopFacetSelect
                                            facets={initialFacets}
                                            selectFacet={selectFacet}
                                            selectedFacet={selectedFacet}
                                        />
                                    </StyledTopFacetSelectWrapper>
                                </StyledTopFilterWrapper>
                                <SearchResult
                                    searchResult={
                                        searchResultList as ISearchHit[]
                                    }
                                />
                                <StyledButtonDiv>
                                    <StyledButton
                                        variant="outlined"
                                        onClick={e => loadMore(e)}
                                        disabled={
                                            (searchResultList?.length ?? 0) <
                                            NUMBER_OF_ARTICLES_TO_LOAD
                                                ? true
                                                : false
                                        }>
                                        {t.search.showMoreHits}{" "}
                                        <Icon name="add" />
                                    </StyledButton>
                                </StyledButtonDiv>
                            </>
                        )}
                    </StyledMain>
                </StyledWrapper>
            ) : (
                <StyledNoResultWrapper>
                    <Typography variant="h5">
                        {t.search.searchNoResult}{" "}
                    </Typography>
                    <Typography>{t.search.searchTips}</Typography>
                </StyledNoResultWrapper>
            )}
        </Layout>
    )
}

/**
 * returns a component containing the search results that have been requested in the
 * searchQuery
 * @param hits returns number of hits in search query
 * @param hasResult a boolean that shows if there are results
 * @param searchQuery a string containing the query being saerched for
 * @returns null if there is no result,
 */

function SearchResultHeader({
    hits,
    hasResult,
    searchQuery
}: {
    hits: number
    hasResult: boolean
    searchQuery: string
}) {
    if (hasResult)
        return (
            <StyledSearchResultHeader>
                <Typography variant="subtitle1">
                    <b>
                        {t.search.searchResultHeader} &apos;{searchQuery}
                        &apos;
                    </b>
                </Typography>

                {hits && (
                    <Typography variant="subtitle2">
                        {replace(t.search.showsHitsFor, {
                            numberOfHits: hits
                        })}
                        &apos;<b>{searchQuery}</b>&apos;
                    </Typography>
                )}
            </StyledSearchResultHeader>
        )

    return null
}

const StyledLeftAside = styled("div")(({ theme }) => ({
    position: "relative",
    paddingBottom: theme.spacing(4),
    display: "none",
    [theme.breakpoints.up("md")]: {
        display: "block"
    }
}))

const StyledTopFilterWrapper = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(4)
    }
}))

const StyledTopFacetSelectWrapper = styled("div")(({ theme }) => ({
    display: "block",
    [theme.breakpoints.up("md")]: {
        display: "none"
    }
}))

const StyledSearchResultHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4)
}))

const StyledNoResultWrapper = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(12),
    [theme.breakpoints.up("sm")]: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
        alignContent: "center",
        justifyContent: "center",
        margin: theme.spacing(4)
    }
}))

const StyledWrapper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    display: "grid",
    gridTemplateColumns: "1fr",
    marginTop: theme.spacing(0),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "1fr 3fr",
        marginTop: theme.spacing(2)
    }
}))

const StyledMain = styled("main")(({ theme }) => ({
    padding: theme.spacing(0, 4, 4, 4),
    overflowY: "auto"
}))

const StyledButtonDiv = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        float: "right"
    }
}))

const StyledButton = styled(Button)(({ theme }) => ({
    gap: theme.spacing(1)
}))

export const getStaticProps = getStaticLayoutProps

const StyledTypographyTooLong = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(2)
}))
